import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Briefcase, Calendar, ChevronRight } from "lucide-react";
import { useLanguage } from "contexts/LanguageContext";

interface Project {
  name: string;
  description: string;
  highlights: string[];
  technologies: string[];
}

interface ExperienceEntry {
  company: string;
  role: string;
  period: string;
  type: string;
  projects: Project[];
}

const Experience: React.FC = () => {
  const { t } = useLanguage();
  const experience: ExperienceEntry[] = t.about.experience;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            {t.experienceSection.badge}
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            {t.experienceSection.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.experienceSection.description}
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-border" />

          <div className="space-y-12">
            {experience.map((entry) => (
              <div key={`${entry.company}-${entry.period}`} className="relative pl-12 md:pl-20">
                {/* Timeline dot */}
                <div className="absolute left-2.5 md:left-6.5 top-2 w-3 h-3 rounded-full bg-primary ring-4 ring-background" />

                {/* Company header */}
                <div className="mb-4">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold">{entry.company}</h3>
                    <Badge variant="outline" className="text-xs">
                      {entry.type === "internship" ? t.experienceSection.internship : t.experienceSection.fullTime}
                    </Badge>
                  </div>
                  <p className="text-base font-medium text-primary">
                    {entry.role}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {entry.period}
                  </p>
                </div>

                {/* Projects */}
                <div className="space-y-4">
                  {entry.projects.map((project) => (
                    <Card key={project.name} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-primary shrink-0" />
                          {project.name}
                        </CardTitle>
                        <CardDescription>{project.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <ul className="space-y-1.5">
                          {project.highlights.map((highlight) => (
                            <li
                              key={highlight}
                              className="text-sm text-muted-foreground flex items-start gap-2"
                            >
                              <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                              {highlight}
                            </li>
                          ))}
                        </ul>
                        <div className="flex flex-wrap gap-1.5 pt-2">
                          {project.technologies.map((tech) => (
                            <Badge key={tech} variant="secondary" className="text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
